import React, { useState } from 'react';
import { projectsApi } from '../../utils/api';
import { validateForm, rules } from '../../utils/validators';
import { Spinner } from '../shared/Spinner';
import { GooglePlacesAutocomplete } from '../shared/GooglePlacesAutocomplete';

interface ProjectCreateFormProps {
  onSuccess: (project: any) => void;
  onCancel: () => void;
}

export const ProjectCreateForm: React.FC<ProjectCreateFormProps> = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    address: '',
    geo: null as { lat: number; lng: number } | null,
    tags: [] as string[],
  });
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = tagInput.trim().toLowerCase();
      
      if (tag && !formData.tags.includes(tag) && formData.tags.length < 10) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tag]
        }));
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddressChange = (result: { address: string; lat?: number; lng?: number }) => {
    setFormData(prev => ({
      ...prev,
      address: result.address,
      geo: result.lat && result.lng ? { lat: result.lat, lng: result.lng } : null
    }));
    
    // Clear address error when user selects/types
    if (errors.address) {
      setErrors(prev => ({ ...prev, address: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    const validationErrors = validateForm(formData, {
      title: [rules.required('Title'), rules.maxLength(200, 'Title')],
      description: [rules.maxLength(2000, 'Description')],
      address: [rules.maxLength(300, 'Address')],
      tags: [], // No validation needed
    } as any);

    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await projectsApi.create({
        ...formData,
        status: 'draft'
      });

      if (response.data.success && response.data.data) {
        onSuccess(response.data.data.project);
      }
    } catch (err: any) {
      const message = err.response?.data?.error?.message || 'Failed to create project';
      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Create New Project</h2>
          <button
            onClick={onCancel}
            className="btn-ghost p-2"
            disabled={loading}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div className="form-group">
            <label htmlFor="title" className="form-label">
              Project Title *
            </label>
            <input
              id="title"
              name="title"
              type="text"
              value={formData.title}
              onChange={handleInputChange}
              className={`input ${errors.title ? 'input-error' : ''}`}
              placeholder="e.g., Villa Deconstruction - Ponsonby"
              disabled={loading}
              required
            />
            {errors.title && <p className="form-error">{errors.title}</p>}
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className={`input min-h-[100px] ${errors.description ? 'input-error' : ''}`}
              placeholder="Describe your deconstruction project, timeline, and what materials you expect to salvage..."
              disabled={loading}
              rows={4}
            />
            {errors.description && <p className="form-error">{errors.description}</p>}
            <p className="form-help">
              Provide details about the project to help potential buyers understand the context and quality of materials.
            </p>
          </div>

          {/* Address */}
          <div className="form-group">
            <label htmlFor="address" className="form-label">
              Project Address
            </label>
            <GooglePlacesAutocomplete
              id="address"
              name="address"
              value={formData.address}
              onChange={handleAddressChange}
              className="input"
              placeholder="Start typing an address in New Zealand..."
              disabled={loading}
              error={!!errors.address}
            />
            {errors.address && <p className="form-error">{errors.address}</p>}
            <p className="form-help">
              Start typing to search for addresses. This helps buyers understand pickup logistics.
            </p>
            {formData.geo && (
              <p className="text-xs text-slate-500 mt-1">
                📍 Location coordinates captured for accurate mapping
              </p>
            )}
          </div>

          {/* Tags */}
          <div className="form-group">
            <label htmlFor="tags" className="form-label">
              Tags
            </label>
            <input
              id="tags"
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleAddTag}
              className="input"
              placeholder="Add tags (press Enter or comma to add)"
              disabled={loading}
            />
            <p className="form-help">
              Add relevant tags like "villa", "commercial", "timber", "heritage", etc.
            </p>
            
            {/* Tag display */}
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-2 text-primary-500 hover:text-primary-700"
                      disabled={loading}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              {errors.submit}
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 flex items-center justify-center"
              disabled={loading || !formData.title.trim()}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Creating...
                </>
              ) : (
                'Create Project'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
